import React, { FC, useEffect, useMemo, useRef } from 'react';

import { ZoomBehavior, ZoomTransform, select, zoom, zoomIdentity } from 'd3';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import Button from '@components/common/Button';

import { Size, Variant } from '@constants/common/ui.constants';

import { ZoomLevels } from '../../types/types';
import useStyles from './styles.module';

import styled from 'styled-components';

const Wrapper = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ count }) => count.toString()}, 1fr);
  grid-gap: 6px;

  position: absolute;
  top: -26px;
  right: 0;
`;

export const ZoomOverlay = styled.rect`
  fill: transparent;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

interface Props {
  svg: SVGElement | null;
  setZoom: (transform: ZoomTransform) => void;
  width: number;
  height: number;
  showResetButton: boolean;
  zoomLevels: ZoomLevels;
}

const Zoom: FC<Props> = ({
  svg,
  setZoom,
  width,
  height,
  showResetButton,
  zoomLevels,
}) => {
  const { classes } = useStyles();
  const zoomBehavior = useRef<ZoomBehavior<Element, unknown>>(null);

  const [zoomIn, zoomOut, zoomInitial] = useMemo(
    () => [
      () =>
        svg &&
        zoomBehavior.current &&
        select(svg as Element)
          .transition()
          .call(zoomBehavior.current.scaleBy, 2),
      () =>
        svg &&
        zoomBehavior.current &&
        select(svg as Element)
          .transition()
          .call(zoomBehavior.current.scaleBy, 0.5),
      () =>
        svg &&
        zoomBehavior.current &&
        select(svg as Element)
          .transition()
          .call(zoomBehavior.current.scaleTo, 0.5),
      () =>
        svg &&
        zoomBehavior.current &&
        select(svg as Element)
          .call(
            zoomBehavior.current.transform,
            zoomIdentity.translate(0, 0).scale(1),
          )
          .transition()
          .call(zoomBehavior.current.scaleTo, 0.5),
    ],
    [svg],
  );

  useEffect(() => {
    if (!svg) return;

    zoomBehavior.current = zoom()
      .scaleExtent([zoomLevels.min, zoomLevels.max])
      .extent([
        [0, 0],
        [width, height],
      ])
      .on('zoom', ({ transform }: { transform: ZoomTransform }) =>
        setZoom(transform),
      );

    select(svg as Element).call(zoomBehavior.current);
  }, [height, width, svg, zoomLevels]);

  useEffect(() => {
    // reset zoom to initial on zoomLevel change
    zoomInitial();
  }, [zoomInitial, zoomLevels]);

  return (
    <Wrapper count={showResetButton ? 3 : 2}>
      <Button
        onClick={zoomIn}
        className={classes.icon}
        variant={Variant.Square}
        size={Size.Small}
      >
        <AddIcon style={{ width: '20px' }} />
      </Button>
      <Button
        variant={Variant.Square}
        onClick={zoomOut}
        size={Size.Small}
        className={classes.icon}
      >
        <RemoveIcon style={{ width: '20px' }} />
      </Button>
    </Wrapper>
  );
};

export default Zoom;
